<script lang="ts">
  /**
   * Sidebar — desktop/tablet navigation (DESIGN-SYSTEM §8.7).
   *
   * Desktop (≥1024px): полноразмерный 240px, пользователь может свернуть
   * до 64px кликом по лого (store sidebarCollapsed, localStorage).
   * В свёрнутом режиме: иконки по центру, тултипы при наведении (data-label).
   * Tablet (768–1023px): всегда иконочный 64px, коллапс-триггер скрыт.
   * Mobile (<768px): скрыт (BottomNav takes over).
   */
  import { _ } from 'svelte-i18n'
  import { page } from '$app/state'
  import { Inbox, BookOpen, Upload, Copy, Tags, ScrollText, Settings, Sun, Moon } from 'lucide-svelte'
  import { theme, setTheme, type Theme } from '$stores/theme'
  import { sidebarCollapsed, toggleSidebar } from '$stores/sidebar'

  const NAV_ITEMS = [
    { href: '/', labelKey: 'nav.queue', icon: Inbox },
    { href: '/bookmarks', labelKey: 'nav.all', icon: BookOpen },
    { href: '/import', labelKey: 'nav.import', icon: Upload },
    { href: '/duplicates', labelKey: 'nav.duplicates', icon: Copy },
    { href: '/tags', labelKey: 'nav.tags', icon: Tags },
    { href: '/audit', labelKey: 'nav.audit', icon: ScrollText },
    { href: '/settings', labelKey: 'nav.settings', icon: Settings },
  ] as const

  const THEME_CYCLE: Record<Theme, Theme> = {
    light: 'dark',
    dark: 'system',
    system: 'light',
  }

  function isActive(href: string): boolean {
    if (href === '/') return page.url.pathname === '/'
    return page.url.pathname.startsWith(href)
  }

  function cycleTheme() {
    setTheme(THEME_CYCLE[$theme])
  }
</script>

<aside class="sidebar" class:collapsed={$sidebarCollapsed}>
  <div class="sidebar-top">
    <button
      type="button"
      class="logo-toggle"
      class:collapsed={$sidebarCollapsed}
      onclick={toggleSidebar}
      title={$sidebarCollapsed ? $_('nav.expand') : $_('nav.collapse')}
      aria-label={$sidebarCollapsed ? $_('nav.expand') : $_('nav.collapse')}
    >
      <span class="logo-icon" aria-hidden="true">📚</span>
      <span class="logo-name">{$_('app.name')}</span>
    </button>
  </div>

  <nav class="nav">
    {#each NAV_ITEMS as item (item.href)}
      <a
        href={item.href}
        class="nav-item"
        class:active={isActive(item.href)}
        data-label={$_(item.labelKey)}
      >
        <item.icon class="nav-icon" size={18} />
        <span class="nav-label">{$_(item.labelKey)}</span>
      </a>
    {/each}
  </nav>

  <div class="sidebar-footer">
    <button class="theme-toggle" onclick={cycleTheme} data-label={$_('settings.theme')}>
      {#if $theme === 'light'}
        <Moon size={16} />
      {:else}
        <Sun size={16} />
      {/if}
      <span class="nav-label">{$_('settings.theme')}</span>
    </button>
  </div>
</aside>

<style>
  .sidebar {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    z-index: 100;
    display: flex;
    flex-direction: column;
    width: 64px;
    background: var(--color-bg-secondary);
    border-right: 1px solid var(--color-border);
    transition: width var(--transition-normal);
  }

  @media (max-width: 767px) {
    .sidebar {
      display: none;
    }
  }

  @media (min-width: 1024px) {
    .sidebar {
      width: var(--sidebar-width);
    }

    .sidebar.collapsed {
      width: var(--sidebar-width-collapsed);
    }
  }

  /* --- Лого-кнопка (триггер сворачивания, desktop only) --- */
  .sidebar-top {
    min-height: var(--header-height);
    display: flex;
    align-items: center;
    padding: 0 var(--space-2);
    border-bottom: 1px solid var(--color-border);
  }

  .logo-toggle {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    width: 100%;
    padding: var(--space-2) var(--space-2);
    border: none;
    background: transparent;
    border-radius: var(--radius-md);
    cursor: pointer;
    color: var(--color-text-primary);
    transition: background-color var(--transition-fast);
  }

  .logo-toggle:hover {
    background: var(--color-bg-tertiary);
  }

  .logo-icon {
    font-size: 20px;
    flex-shrink: 0;
    width: 20px;
    text-align: center;
  }

  .logo-name {
    font-size: var(--text-sm);
    font-weight: 700;
    color: var(--color-text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: opacity var(--transition-fast);
  }

  @media (max-width: 1023px) {
    .logo-toggle {
      justify-content: center;
      padding: var(--space-2) 0;
    }
  }

  /* Свёрнутое состояние (desktop) */
  @media (min-width: 1024px) {
    .sidebar.collapsed .logo-toggle {
      justify-content: center;
      padding: var(--space-2) 0;
    }

    .sidebar.collapsed .logo-name {
      opacity: 0;
      width: 0;
    }
  }

  .nav {
    flex: 1;
    padding: var(--space-3) 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .nav-item {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    height: 40px;
    margin: 0 var(--space-2);
    padding: 0 var(--space-3);
    border-radius: var(--radius-md);
    color: var(--color-text-secondary);
    text-decoration: none;
    position: relative;
    transition:
      background-color var(--transition-fast),
      color var(--transition-fast);
  }

  .nav-item:hover {
    background: var(--color-bg-tertiary);
    color: var(--color-text-primary);
    text-decoration: none;
  }

  .nav-item.active {
    background: var(--color-accent-light);
    color: var(--color-accent);
  }

  .nav-label {
    font-size: var(--text-sm);
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    transition: opacity var(--transition-fast);
  }

  @media (max-width: 1023px) {
    .nav-item {
      justify-content: center;
      padding: 0;
    }

    .nav-label {
      display: none;
    }
  }

  /* Свёрнутое состояние (desktop): иконки по центру + тултипы */
  @media (min-width: 1024px) {
    .sidebar.collapsed .nav-item {
      justify-content: center;
      padding: 0;
    }

    .sidebar.collapsed .nav-label {
      opacity: 0;
      width: 0;
    }

    .sidebar.collapsed .nav-item::after,
    .sidebar.collapsed .theme-toggle::after {
      content: attr(data-label);
      position: absolute;
      left: calc(100% + 12px);
      top: 50%;
      transform: translateY(-50%) translateX(-4px);
      background: var(--color-bg-tertiary);
      border: 1px solid var(--color-border);
      color: var(--color-text-primary);
      padding: 5px 10px;
      border-radius: 6px;
      font-size: 12px;
      white-space: nowrap;
      box-shadow: var(--shadow-md);
      opacity: 0;
      pointer-events: none;
      transition:
        opacity var(--transition-fast),
        transform var(--transition-fast);
      z-index: 200;
    }

    .sidebar.collapsed .nav-item:hover::after,
    .sidebar.collapsed .theme-toggle:hover::after {
      opacity: 1;
      transform: translateY(-50%) translateX(0);
    }
  }

  .sidebar-footer {
    padding: var(--space-3) 0;
    border-top: 1px solid var(--color-border);
  }

  .theme-toggle {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    width: 100%;
    height: 40px;
    margin: 0 var(--space-2);
    padding: 0 var(--space-3);
    border: none;
    background: transparent;
    color: var(--color-text-secondary);
    border-radius: var(--radius-md);
    cursor: pointer;
    position: relative;
    transition:
      background-color var(--transition-fast),
      color var(--transition-fast);
  }

  .theme-toggle:hover {
    background: var(--color-bg-tertiary);
    color: var(--color-text-primary);
  }

  .theme-toggle .nav-label {
    width: calc(var(--sidebar-width) - 64px);
  }

  @media (max-width: 1023px) {
    .theme-toggle {
      justify-content: center;
      padding: 0;
    }

    .theme-toggle .nav-label {
      display: none;
    }
  }

  @media (min-width: 1024px) {
    .sidebar.collapsed .theme-toggle {
      justify-content: center;
      padding: 0;
    }

    .sidebar.collapsed .theme-toggle .nav-label {
      opacity: 0;
      width: 0;
    }
  }
</style>
